/** Helpers partagés pour l'agenda (événements + réunions convoquées). */

export function getAgendaItemDate(item) {
  if (item.kind === 'reunion') {
    return new Date(item.date);
  }
  if (!item.dates?.length) return new Date(8640000000000000);
  const sorted = [...item.dates].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  return new Date(sorted[0].startDate);
}

export function getAgendaItemEndDate(item) {
  if (item.kind === 'reunion') {
    const d = new Date(item.date);
    if (item.heureFin) {
      const [h, m] = item.heureFin.split(':').map(Number);
      d.setHours(h || 23, m || 59, 59, 999);
    } else {
      d.setHours(23, 59, 59, 999);
    }
    return d;
  }
  const sorted = [...(item.dates || [])].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  if (!sorted.length) return new Date(0);
  const last = sorted[sorted.length - 1];
  if (last.endDate) return new Date(last.endDate);
  const d = new Date(last.startDate);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function isAgendaItemPast(item) {
  return new Date() > getAgendaItemEndDate(item);
}

export function mergeAgendaItems(events = [], reunions = []) {
  const items = [
    ...events.map((e) => ({ ...e, kind: 'event' })),
    ...reunions.map((r) => ({ ...r, kind: 'reunion' })),
  ];
  return items.sort((a, b) => getAgendaItemDate(a) - getAgendaItemDate(b));
}

export function getOrganismeLabel(organisme) {
  const labels = {
    SAR: 'Syndicat Apicole de la Réunion',
    AMAIR: 'Association de la Maison de l\'Apiculture de l\'Île de la Réunion',
    ADAR: 'ADAR',
  };
  return labels[organisme] || organisme;
}

const ORGANISME_LOGOS = {
  SAR: '/logos/logo_sar.png',
  AMAIR: '/logos/logo_amair.png',
  ADAR: '/logos/logo_adar.png',
};

export function getOrganismeLogo(organisme) {
  return ORGANISME_LOGOS[organisme] || null;
}

export function getEventStatusBadge(ev) {
  const now = new Date();
  if (ev.status === 'annule') return { label: 'Annulé', cls: 'bg-red-100 text-red-800' };
  if (ev.status === 'brouillon') return { label: 'Non publié', cls: 'bg-gray-100 text-gray-600' };
  const sorted = [...(ev.dates || [])].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  if (!sorted.length) return { label: 'Publié', cls: 'bg-green-100 text-green-800' };
  const firstStart = new Date(sorted[0].startDate);
  const lastEnd = getAgendaItemEndDate(ev);
  if (now > lastEnd) return { label: 'Passé', cls: 'bg-slate-100 text-slate-600' };
  if (now >= firstStart) return { label: 'En cours', cls: 'bg-green-100 text-green-800' };
  const dl = firstStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  return { label: `Commence le ${dl}`, cls: 'bg-indigo-100 text-indigo-800' };
}

export function getReunionStatusBadge(reunion) {
  const now = new Date();
  const start = new Date(reunion.date);
  if (reunion.heureDebut) {
    const [h, m] = reunion.heureDebut.split(':').map(Number);
    start.setHours(h || 0, m || 0, 0, 0);
  }
  const end = getAgendaItemEndDate(reunion);
  if (now > end) return { label: 'Réunion passée', cls: 'bg-slate-100 text-slate-600' };
  if (now >= start) return { label: 'Réunion en cours', cls: 'bg-honey-100 text-honey-800' };
  const dl = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  const timeLabel = reunion.heureDebut ? ` à ${reunion.heureDebut}` : '';
  return { label: `Réunion le ${dl}${timeLabel}`, cls: 'bg-honey-100 text-honey-800' };
}

export function getAgendaStatusBadge(item) {
  if (item.kind === 'reunion') return getReunionStatusBadge(item);
  return getEventStatusBadge(item);
}

/** Retourne 'YYYY-MM-DD' de chaque jour couvert par un item d'agenda. */
export function agendaItemDateStrings(item) {
  const out = new Set();
  const isoDate = (d) => d.toISOString().split('T')[0];

  if (item.kind === 'reunion') {
    out.add(isoDate(new Date(item.date)));
    return out;
  }

  (item.dates || []).forEach((d) => {
    const start = new Date(d.startDate);
    const end = d.endDate ? new Date(d.endDate) : new Date(d.startDate);
    const cur = new Date(start);
    while (cur <= end) {
      out.add(isoDate(cur));
      cur.setDate(cur.getDate() + 1);
    }
  });
  return out;
}
